import React, { useEffect, useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import Drawer from '../../shared/ui/Drawer.js';
import { apiFetch } from '../../lib/api-client.js';
import { meetingStatusPresentation } from './safety-ui.js';
import SafetySignatureCanvas from './SafetySignatureCanvas.js';
import { useSafetyWrite } from './use-safety-write.js';
import type { SafetyMeeting } from './use-safety-read-model.js';

interface SafetyMeetingDetailDrawerProps {
  meetingId: string | number | null;
  onClose: () => void;
  onMeetingClosed?: () => void;
  onEnterKiosk?: (meetingId: string | number) => void;
}

/**
 * Signature shape matches the worker response from GET /api/safety-meeting/:id
 * (see workers/api/src/index.js handleSafetyMeetingDetail). Each row is
 * returned as { id, employee_name, signed_at } — employee_name is already
 * resolved server-side via the employees table join.
 */
interface MeetingSignature {
  id?: string | number;
  employee_name?: string;
  signed_at?: string;
}

/**
 * Worker response is top-level { meeting, signatures } — signatures are NOT
 * nested inside meeting. Typing it explicitly here avoids the silent data
 * loss that previously made every meeting look unsigned in the drawer.
 */
interface MeetingDetailResponse {
  meeting?: SafetyMeeting;
  signatures?: MeetingSignature[];
  error?: string;
}

export default function SafetyMeetingDetailDrawer({
  meetingId,
  onClose,
  onMeetingClosed,
  onEnterKiosk,
}: SafetyMeetingDetailDrawerProps) {
  const [meeting, setMeeting] = useState<SafetyMeeting | null>(null);
  const [signatures, setSignatures] = useState<MeetingSignature[]>([]);
  const [loading, setLoading] = useState(false);
  const [driverIdInput, setDriverIdInput] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signatureResetKey, setSignatureResetKey] = useState(0);
  const [signSuccess, setSignSuccess] = useState<string | null>(null);

  const [showCloseForm, setShowCloseForm] = useState(false);
  const [closeCode, setCloseCode] = useState('');
  const [closeDriverId, setCloseDriverId] = useState('');
  const {
    forceCloseMeeting,
    signMeeting,
    submitting,
    error,
    clearError,
  } = useSafetyWrite(() => {
    /* parent-driven refresh via onMeetingClosed */
  });

  const closeSubmitting = submitting && showCloseForm;
  const signSubmitting = submitting && !showCloseForm;
  const closeError = showCloseForm ? error : null;
  const signError = showCloseForm ? null : error;

  function resetSignForm() {
    setDriverIdInput('');
    setSignatureData(null);
    setSignatureResetKey((current) => current + 1);
  }

  async function loadMeetingDetail(activeMeetingId: string | number) {
    const data = await apiFetch<MeetingDetailResponse>(`/api/safety-meeting/${activeMeetingId}`);
    setMeeting(data.meeting || null);
    setSignatures(Array.isArray(data.signatures) ? data.signatures : []);
  }

  useEffect(() => {
    if (!meetingId) {
      setMeeting(null);
      setSignatures([]);
      setShowCloseForm(false);
      setCloseCode('');
      setCloseDriverId('');
      setDriverIdInput('');
      setSignatureData(null);
      setSignSuccess(null);
      setSignatureResetKey((current) => current + 1);
      clearError();
      return;
    }
    setLoading(true);
    setDriverIdInput('');
    setSignatureData(null);
    setSignSuccess(null);
    setSignatureResetKey((current) => current + 1);
    loadMeetingDetail(meetingId)
      .catch(() => {
        setMeeting(null);
        setSignatures([]);
      })
      .finally(() => setLoading(false));
  // clearError is stable across renders; including it would cause stale-closure churn — intentionally omitted.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  async function handleForceClose() {
    if (!meetingId) return;
    if (closeCode.trim().length < 7) return;
    if (closeDriverId.trim().length !== 7) return;
    const ok = await forceCloseMeeting(meetingId, closeCode.trim(), closeDriverId.trim());
    if (ok) {
      setShowCloseForm(false);
      setCloseCode('');
      setCloseDriverId('');
      if (onMeetingClosed) onMeetingClosed();
    }
  }

  function cancelCloseForm() {
    setShowCloseForm(false);
    setCloseCode('');
    setCloseDriverId('');
    clearError();
  }

  async function handleSignMeeting() {
    if (!meetingId || driverIdInput.trim().length !== 7 || !signatureData) return;
    const result = await signMeeting(meetingId, driverIdInput.trim(), signatureData);
    if (!result || typeof result !== 'object') return;

    setSignSuccess(result.name || 'Signature recorded');
    resetSignForm();
    try {
      await loadMeetingDetail(meetingId);
    } catch {
      setSignSuccess('Signature recorded, but refresh failed');
    }
    window.setTimeout(() => setSignSuccess(null), 1500);
  }

  if (!meetingId) return null;

  const status = meeting ? meetingStatusPresentation(meeting.status ?? '') : null;

  const drawerTitle = meeting
    ? `Safety meeting · Terminal ${meeting.terminal_number || '—'}`
    : 'Safety meeting';

  return (
    <Drawer open={true} onClose={onClose} title={drawerTitle} width={420}>
      {loading ? (
        <div style={{ color: C.textMuted, marginTop: 40 }}>Loading meeting...</div>
      ) : !meeting ? (
        <div style={{ color: C.red, marginTop: 40 }}>Meeting not found.</div>
      ) : (
        <>
          <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
            Terminal {meeting.terminal_number || '—'}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: status!.color, background: status!.background }}>{status!.label}</span>
            <span style={{ color: C.textMuted, fontSize: 12 }}>{signatures.length} signature{signatures.length !== 1 ? 's' : ''}</span>
          </div>

          {meeting.status === 'open' && meeting.id != null && onEnterKiosk && (
            <button
              type="button"
              onClick={() => onEnterKiosk(meeting.id!)}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: 16 }}
            >
              Enter kiosk mode
            </button>
          )}

          <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Topic',    value: meeting.topic || '—' },
              { label: 'Opened',   value: meeting.opened_at ? String(meeting.opened_at).slice(0, 16).replace('T', ' ') : '—' },
              { label: 'Closed',   value: meeting.closed_at ? String(meeting.closed_at).slice(0, 16).replace('T', ' ') : 'Still open' },
            ].map(({ label, value }) => (
              <div key={label} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 3 }}>{label}</div>
                <div style={{ color: C.text, fontSize: 13 }}>{value}</div>
              </div>
            ))}
          </div>

          {meeting.status === 'open' && (
            <div style={{ marginBottom: 16, padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 13, marginBottom: 8 }}>Sign attendance</div>
              <label className="field-label" htmlFor="safety-sign-driver-id">Driver ID</label>
              <input
                id="safety-sign-driver-id"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={7}
                value={driverIdInput}
                onChange={(e) => {
                  setDriverIdInput(e.target.value.replace(/\D/g, '').slice(0, 7));
                  clearError();
                }}
                placeholder="7-digit driver ID"
                className="field-input"
                style={{ marginBottom: 10 }}
              />

              <label className="field-label">Signature</label>
              <SafetySignatureCanvas resetKey={signatureResetKey} onSignatureChange={setSignatureData} />

              {signError && (
                <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{signError}</div>
              )}
              {signSuccess && (
                <div style={{ color: C.green, fontSize: 12, marginTop: 10 }}>{signSuccess}</div>
              )}

              <button
                type="button"
                onClick={handleSignMeeting}
                className="btn btn-primary"
                disabled={signSubmitting || driverIdInput.trim().length !== 7 || !signatureData}
                style={{ width: '100%', marginTop: 12 }}
              >
                {signSubmitting ? 'Submitting…' : 'Submit attendance signature'}
              </button>
            </div>
          )}

          {meeting.status === 'open' && (
            <div style={{ marginBottom: 16, padding: 12, borderRadius: 10, background: 'rgba(201,162,39,0.06)', border: '1px solid rgba(201,162,39,0.25)' }}>
              {!showCloseForm ? (
                <button
                  type="button"
                  onClick={() => { clearError(); setShowCloseForm(true); }}
                  className="btn btn-secondary"
                  style={{ width: '100%' }}
                >
                  Force close meeting
                </button>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  <div style={{ color: C.gold, fontSize: 12, letterSpacing: '0.04em' }}>Manager override</div>
                  <label className="field-label" htmlFor="safety-close-code">Close code</label>
                  <input
                    id="safety-close-code"
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={closeCode}
                    onChange={(e) => setCloseCode(e.target.value)}
                    placeholder="Close code"
                    className="field-input"
                  />
                  <label className="field-label" htmlFor="safety-close-driver-id">Your driver ID</label>
                  <input
                    id="safety-close-driver-id"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={7}
                    value={closeDriverId}
                    onChange={(e) => setCloseDriverId(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    placeholder="7-digit driver ID"
                    className="field-input"
                  />
                  {closeError && (
                    <div style={{ color: C.red, fontSize: 12 }}>{closeError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button
                      type="button"
                      onClick={cancelCloseForm}
                      className="btn btn-ghost"
                      disabled={closeSubmitting}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleForceClose}
                      className="btn btn-primary"
                      disabled={closeSubmitting || closeCode.trim().length < 7 || closeDriverId.trim().length !== 7}
                      style={{ flex: 1 }}
                    >
                      {closeSubmitting ? 'Closing…' : 'Close meeting'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {signatures.length > 0 && (
            <div>
              <div style={{ color: C.gold, fontSize: 13, marginBottom: 8 }}>Attendance ({signatures.length})</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {signatures.map((sig: MeetingSignature, idx: number) => (
                  <div key={sig.id || idx} style={{ padding: 8, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                    <div style={{ color: C.text, fontSize: 12 }}>{sig.employee_name || `Signer ${idx + 1}`}</div>
                    {sig.signed_at && <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{String(sig.signed_at).slice(0, 16).replace('T', ' ')}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
